import React, { useMemo } from 'react';
import { MARKETPLACE_CONFIGS } from '../configs/marketplaceConfigs';
import {
  calculateDaysSupplied,
  calculateDueDate,
  getDueDateClass,
  renderDaysSuppliedCell,
} from '../utils/yoyCalculations';

const ALL_MARKETPLACES = ['amazon', 'walmart', 'shopify', 'etsy'];

// Fixed left column count: product name + identifier + total stock + days supplied + due date + plan to produce = 6
const FIXED_COL_COUNT = 6;

// Returns the correct identifier for a product in a given marketplace
// Amazon uses asin, all others use sku
const getIdentifierForMp = (product, mp) => {
  const field = MARKETPLACE_CONFIGS[mp]?.identifierField ?? 'asin';
  return product[field];
};

const YoySummaryTable = ({
  filteredProducts,
  identifierField,
  currentWeekInfo,
  historicalWeeks,
  forecastWeeks,
  historicalWeeksCount,
  forecastWeeksCount,
  visibleColumns,
  salesDataByMarketplace,
  weeklyPlanData,
  planToProduceData,
  showStockBalanceRow,
  calculateForecastForProductConditional,
  calculateAverageGrowthForProduct,
}) => {

  // ─── Helpers ───────────────────────────────────────────────────────────────

  /**
   * Sum sales across all marketplaces for a product/year/week.
   * Uses the correct identifier per marketplace (asin for Amazon, sku for others).
   */
  const sumSales = (product, year, week) => {
    let total = 0;
    let hasAny = false;
    for (const mp of ALL_MARKETPLACES) {
      const id = getIdentifierForMp(product, mp);
      if (!id) continue;
      const key = `${id}-${year}-W${week}`;
      const v = salesDataByMarketplace[mp]?.[key]?.value;
      if (v !== undefined && v !== null && v !== 'N/A') {
        total += Number(v);
        hasAny = true;
      }
    }
    return hasAny ? total : 'N/A';
  };

  /**
   * Sum forecast across all marketplaces for a product/week/year.
   * Each marketplace uses its own logic (seasonality / median / YoY).
   */
  const sumForecast = (product, weekNumber, year, avgGrowthByMp) => {
    let total = 0;
    let hasAny = false;
    for (const mp of ALL_MARKETPLACES) {
      const id = getIdentifierForMp(product, mp);
      if (!id) continue;
      const avgGrowth = avgGrowthByMp[mp] ?? 0;
      const v = calculateForecastForProductConditional(product, weekNumber, year, avgGrowth, mp);
      if (v !== undefined && v !== null && v !== 'N/A') {
        total += Number(v);
        hasAny = true;
      }
    }
    return hasAny ? total : 'N/A';
  };

  /** Total stock = WH + AWD + all marketplace stock columns (deduped). */
  const getTotalStock = (product) => {
    let total = Number(product.whStock) || 0;
    total += Number(product.awdStock) || 0;
    const seenKeys = new Set();
    for (const mp of ALL_MARKETPLACES) {
      const config = MARKETPLACE_CONFIGS[mp];
      if (!config?.stockColumns) continue;
      for (const col of config.stockColumns) {
        if (!seenKeys.has(col.key)) {
          seenKeys.add(col.key);
          total += Number(product[col.key]) || 0;
        }
      }
    }
    return total;
  };

  // ─── Pre-compute avgGrowth per product per marketplace ──────────────────────
  const avgGrowthMap = useMemo(() => {
    const map = {};
    for (const product of filteredProducts) {
      const primaryId = product[identifierField];
      map[primaryId] = {};
      for (const mp of ALL_MARKETPLACES) {
        const id = getIdentifierForMp(product, mp);
        if (id) {
          map[primaryId][mp] = calculateAverageGrowthForProduct(id, mp);
        }
      }
    }
    return map;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredProducts, identifierField]);

  // ─── Est. End-of-Week Stock ───────────────────────────────────────────────
  // Stores forecast per week so calcWeeksOfStock can use it later
  const computeStockBalances = (product, avgGrowthByMp) => {
    const sku = product.sku;
    const initialStock = getTotalStock(product);

    const weeksToCalc = currentWeekInfo
      ? [{ weekNumber: currentWeekInfo.weekNumber, year: currentWeekInfo.year }, ...forecastWeeks]
      : [];

    const balances = {};
    let prevBalance = initialStock;

    for (const week of weeksToCalc) {
      const weekKey = `${week.year}-W${week.weekNumber}`;
      const production = (weeklyPlanData[sku] || {})[weekKey] || 0;
      const forecastSum = sumForecast(product, week.weekNumber, week.year, avgGrowthByMp);
      const forecastNum = forecastSum !== 'N/A' ? forecastSum : 0;

      const startBalance = Math.max(0, prevBalance);
      const endBalance = startBalance + production - forecastNum;

      balances[weekKey] = {
        endBalance,
        forecast: forecastNum, // stored so calcWeeksOfStock can iterate over it
        tooltip: `Start: ${startBalance.toLocaleString()}\n+ Production: ${production.toLocaleString()}\n− Forecast (all): ${forecastNum.toLocaleString()}\n= ${endBalance.toLocaleString()}`,
      };
      prevBalance = endBalance;
    }
    return balances;
  };

  // ─── Days supplied (total stock vs summed forecasts) ─────────────────────
  const computeTotalDaysSupplied = (product, avgGrowthByMp) => {
    const identifier = product[identifierField];
    const totalStock = getTotalStock(product);

    const forecastFn = (_id, weekNumber, year) =>
      sumForecast(product, weekNumber, year, avgGrowthByMp);
    const getSalesForCalc = (_id, year, week) => sumSales(product, year, week);

    return calculateDaysSupplied(
      totalStock,
      identifier,
      null,
      null,
      currentWeekInfo,
      () => 0,
      forecastFn,
      getSalesForCalc
    );
  };

  // ─── Render helpers ───────────────────────────────────────────────────────
  const fmt = (v) => (v === 'N/A' || v === null || v === undefined) ? 'N/A' : v;

  const pctChange = (current, previous) => {
    if (current === 'N/A' || previous === 'N/A' || Number(previous) === 0) return null;
    const pct = Math.round(((Number(current) - Number(previous)) / Number(previous)) * 100);
    return (
      <span className={`change-indicator ${pct >= 0 ? 'positive' : 'negative'}`}>
        {pct >= 0 ? '+' : ''}{pct}%
      </span>
    );
  };

  // How many future forecast weeks does endBalance cover
  const calcWeeksOfStock = (endBalance, stockBalances) => {
    if (endBalance <= 0) return 0;
    let remaining = endBalance;
    let weeks = 0;
    for (const fw of forecastWeeks) {
      const fwKey = `${fw.year}-W${fw.weekNumber}`;
      const fwForecast = stockBalances[fwKey]?.forecast ?? 0;
      if (fwForecast <= 0 || remaining >= fwForecast) {
        remaining -= fwForecast > 0 ? fwForecast : 0;
        weeks++;
      } else {
        break;
      }
    }
    return weeks;
  };

  // Renders a stock balance cell with endBalance and /Nw suffix
  const renderBalanceCell = (entry, extraClass = '', stockBalances = {}) => {
    if (!entry) {
      return <td className={`stock-balance-cell stock-balance-empty ${extraClass}`}>—</td>;
    }
    const { endBalance, tooltip } = entry;
    const cls = endBalance < 0 ? 'balance-negative' : endBalance === 0 ? 'balance-zero' : 'balance-positive';
    const weeksOfStock = calcWeeksOfStock(endBalance, stockBalances);
    return (
      <td className={`stock-balance-cell ${extraClass} ${cls}`} title={tooltip}>
        {endBalance.toLocaleString()}
        <span className="stock-weeks-suffix" style={{ opacity: 0.65, fontSize: '0.85em', marginLeft: '3px' }}>
          /{weeksOfStock}w
        </span>
      </td>
    );
  };

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="table-responsive summary-table-wrapper">
      <table className="yoy-table summary-table">
        <thead>
          <tr>
            <th rowSpan="2" className="yoy-fixed-column product-name-head">Product Name</th>
            <th rowSpan="2">{identifierField.toUpperCase()}</th>
            <th rowSpan="2">Total Stock</th>
            <th rowSpan="2">Days Supplied</th>
            <th rowSpan="2">Due Date</th>
            <th rowSpan="2">Plan to<br />Produce</th>

            {visibleColumns.historical && historicalWeeks.length > 0 && (
              <th colSpan={historicalWeeks.length} className="section-header">
                Historical Data ({historicalWeeksCount} weeks)
              </th>
            )}

            {visibleColumns.currentWeek && currentWeekInfo && (
              <th rowSpan="2" className="current-week-header section-header">
                Current Week (W{currentWeekInfo.weekNumber} {currentWeekInfo.year})
                <div style={{ fontSize: '10px', fontWeight: 'normal' }}>
                  {currentWeekInfo.mondayDate} - {currentWeekInfo.sundayDate}
                </div>
              </th>
            )}

            {visibleColumns.prediction && forecastWeeks.length > 0 && (
              <th colSpan={forecastWeeks.length} className="section-header">
                Prediction ({forecastWeeksCount} weeks)
              </th>
            )}
          </tr>

          <tr>
            {visibleColumns.historical && historicalWeeks.map((week, idx) => (
              <th key={idx} className="week-header">
                <div>W{week.weekNumber} {week.year}</div>
                <div style={{ fontSize: '9px', fontWeight: 'normal' }}>{week.dateRange}</div>
              </th>
            ))}
            {visibleColumns.prediction && forecastWeeks.map((week, idx) => (
              <th key={idx} className="prediction-header">
                W{week.weekNumber} {week.year}
              </th>
            ))}
          </tr>
        </thead>

        <tbody>
          {filteredProducts.map(product => {
            const identifier = product[identifierField];
            const avgGrowthByMp = avgGrowthMap[identifier] || {};
            const totalStock = getTotalStock(product);
            const totalDays = computeTotalDaysSupplied(product, avgGrowthByMp) || 0;
            const stockBalances = showStockBalanceRow ? computeStockBalances(product, avgGrowthByMp) : null;
            const currentKey = currentWeekInfo ? `${currentWeekInfo.year}-W${currentWeekInfo.weekNumber}` : null;
            const planQty = planToProduceData?.[product.sku] || 0;

            const fixedLeftCells = (
              <>
                <td rowSpan="2" className="yoy-fixed-column product-cell">{product.productName}</td>
                <td rowSpan="2" className="identifier-cell">{identifier}</td>
                <td rowSpan="2" className="stock-cell">{totalStock.toLocaleString()}</td>
                <td rowSpan="2" className="days-supplied-total-cell">
                  {renderDaysSuppliedCell(totalDays)}
                </td>
                <td rowSpan="2" className="due-date-cell">
                  <div className={`due-date-value ${getDueDateClass(totalDays)}`}>
                    {calculateDueDate(totalDays)}
                  </div>
                </td>
                <td rowSpan="2" className="plan-to-produce-cell">
                  {planQty > 0 ? planQty.toLocaleString() : '—'}
                </td>
              </>
            );

            return (
              <React.Fragment key={product.id}>

                {/* ── Previous Year Row ──────────────────────────────── */}
                <tr className="previous-year-row">
                  {fixedLeftCells}

                  {visibleColumns.historical && historicalWeeks.map((week, idx) => (
                    <td key={idx} className="historical-cell previous-year">
                      {fmt(sumSales(product, week.year - 1, week.weekNumber))}
                    </td>
                  ))}

                  {visibleColumns.currentWeek && currentWeekInfo && (
                    <td className="current-week-cell previous-year">
                      {fmt(sumSales(product, currentWeekInfo.year - 1, currentWeekInfo.weekNumber))}
                    </td>
                  )}

                  {visibleColumns.prediction && forecastWeeks.map((week, idx) => (
                    <td key={idx} className="prediction-cell previous-year">
                      {fmt(sumSales(product, week.year - 1, week.weekNumber))}
                    </td>
                  ))}
                </tr>

                {/* ── Current Year Row ───────────────────────────────── */}
                <tr className="current-year-row">
                  {visibleColumns.historical && historicalWeeks.map((week, idx) => {
                    const cur = sumSales(product, week.year, week.weekNumber);
                    const prev = sumSales(product, week.year - 1, week.weekNumber);
                    return (
                      <td key={idx} className="historical-cell">
                        <div className="cell-content">
                          <div className="value-with-edit"><span>{fmt(cur)}</span></div>
                          {pctChange(cur, prev)}
                        </div>
                      </td>
                    );
                  })}

                  {visibleColumns.currentWeek && currentWeekInfo && (
                    <td className="current-week-cell">
                      <div className="value-with-edit">
                        {fmt(sumForecast(product, currentWeekInfo.weekNumber, currentWeekInfo.year, avgGrowthByMp))}
                      </div>
                    </td>
                  )}

                  {visibleColumns.prediction && forecastWeeks.map((week, idx) => {
                    const forecast = sumForecast(product, week.weekNumber, week.year, avgGrowthByMp);
                    const previous = sumSales(product, week.year - 1, week.weekNumber);
                    return (
                      <td key={idx} className="prediction-cell">
                        <div className="current-year-value">
                          {fmt(forecast)}
                          {pctChange(forecast, previous)}
                        </div>
                      </td>
                    );
                  })}
                </tr>

                {/* ── Est. End-of-Week Stock Row ─────────────────────── */}
                {showStockBalanceRow && currentWeekInfo && stockBalances && (
                  <tr className="stock-balance-row">
                    <td colSpan={FIXED_COL_COUNT} className="stock-balance-label-cell">
                      <span className="stock-balance-label">Est. End-of-Week Stock</span>
                    </td>

                    {visibleColumns.historical && historicalWeeks.map((_, idx) => (
                      <td key={idx} className="stock-balance-cell stock-balance-empty" />
                    ))}

                    {visibleColumns.currentWeek && renderBalanceCell(
                      stockBalances[currentKey], 'current-week-cell', stockBalances
                    )}

                    {visibleColumns.prediction && forecastWeeks.map((week, idx) => (
                      React.cloneElement(
                        renderBalanceCell(
                          stockBalances[`${week.year}-W${week.weekNumber}`],
                          'prediction-cell',
                          stockBalances
                        ),
                        { key: idx }
                      )
                    ))}
                  </tr>
                )}

              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default YoySummaryTable;